import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import "../Styles/DateSessionSelector.css";
import axios from "axios";
import { WiDaySunny } from "react-icons/wi";
import { IoMoonOutline } from "react-icons/io5";
import { MdOutlineTimer } from "react-icons/md";
import { FaHeart } from "react-icons/fa";
import bowlImg from "../assets/bowl.png";
import {
  SESSION_ORDER,
  HORIZON_DAYS,
  addDays,
  buildAvailabilityMap,
  dateKey,
  dayLabel,
  formatCutoff,
  getCutoff,
  getWindowEnd,
  hasMenu,
  normalizeToUTCMidnight,
  resolveSessionSlot,
} from "../Helper/sessionAvailability";

const sessionConfig = {
  Breakfast: {
    icon: WiDaySunny,
    deliveryTime: "7-8 AM",
    displayName: "Breakfast",
  },
  Lunch: { icon: WiDaySunny, deliveryTime: "12-1 PM", displayName: "Lunch" },
  Dinner: {
    icon: IoMoonOutline,
    deliveryTime: "7-8 PM",
    displayName: "Dinner",
  },
};

const DateSessionSelector = ({
  onChange,
  currentDate,
  currentSession,
  menuData = [],
  hubCutoffData = null, // passed from Home — avoids duplicate fetch
  menuLoading = false, // true while Home is still fetching allHubMenuData
  onAvailabilityChange, // notifies parent so it can suppress its own "no items" message
  windowItems = null, // live { session: portionsLeft } for the selected session, from Home
  noAddress = false, // true when a logged-in user has no delivery address set
}) => {
  const navigate = useNavigate();

  const getUser = () => {
    try {
      return JSON.parse(localStorage.getItem("user"));
    } catch {
      return null;
    }
  };

  const user = getUser();
  const isEmployee = user?.status === "Employee";

  // Use prop if provided, otherwise fall back to internal fetch
  const [hubDataInternal, setHubDataInternal] = useState(null);
  const hubData = hubCutoffData || hubDataInternal;

  const [currentHubId, setCurrentHubId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());

  // Update time every second for precise cutoff checking
  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  const getHubIdFromAddress = () => {
    try {
      const primaryAddress = localStorage.getItem("primaryAddress");
      const currentLocation = localStorage.getItem("currentLocation");
      if (primaryAddress && primaryAddress !== "null") {
        const parsed = JSON.parse(primaryAddress);
        return parsed.hubId;
      }
      if (currentLocation && currentLocation !== "null") {
        const parsed = JSON.parse(currentLocation);
        return parsed.hubId;
      }
    } catch (error) {
      console.error("Error getting hub ID:", error);
    }
    return null;
  };

  const fetchHubData = async (hubId) => {
    if (!hubId) return null;
    setLoading(true);
    try {
      const token = localStorage.getItem("authToken");
      const res = await axios.get(
        `https://dailydish.in/api/Hub/get-cutoff-times/${hubId}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (res.status === 200) {
        const cutoffTimes = res.data.cutoffTimes || {};
        const orderMode = res.data.orderMode || "preorder";

        // employeeCutoff is left as the backend sends it — getCutoff() falls back
        // to defaultCutoff when it is absent.
        ["breakfast", "lunch", "dinner"].forEach((session) => {
          if (!cutoffTimes[session]) {
            cutoffTimes[session] = {};
          }
          if (!cutoffTimes[session].defaultCutoff) {
            cutoffTimes[session].defaultCutoff =
              orderMode === "instant" ? "20:00" : "23:59";
          }
        });

        return { orderMode, cutoffTimes };
      }
    } catch (error) {
      console.error("Error fetching hub data:", error);
      return {
        orderMode: "preorder",
        cutoffTimes: {
          breakfast: { defaultCutoff: "23:59" },
          lunch: { defaultCutoff: "23:59" },
          dinner: { defaultCutoff: "23:59" },
        },
      };
    } finally {
      setLoading(false);
    }
    return null;
  };

  useEffect(() => {
    const handleLocationChange = async () => {
      // If parent is providing hubCutoffData, skip internal fetch entirely
      if (hubCutoffData) return;

      const hubId = getHubIdFromAddress();
      if (hubId) {
        if (hubId !== currentHubId) {
          setCurrentHubId(hubId);
          const data = await fetchHubData(hubId);
          if (data) {
            setHubDataInternal(data);
          }
        }
      }
    };

    handleLocationChange();

    window.addEventListener("locationUpdated", handleLocationChange);
    window.addEventListener("addressUpdated", handleLocationChange);
    window.addEventListener("storage", handleLocationChange);

    return () => {
      window.removeEventListener("locationUpdated", handleLocationChange);
      window.removeEventListener("addressUpdated", handleLocationChange);
      window.removeEventListener("storage", handleLocationChange);
    };
  }, [currentHubId, hubCutoffData]);

  const availability = useMemo(
    () => buildAvailabilityMap(menuData),
    [menuData],
  );

  const todayObj = useMemo(
    () => normalizeToUTCMidnight(currentTime),
    [currentTime],
  );

  // Home.jsx owns the selected date/session state and handles all auto-selection logic.
  // DateSessionSelector is a controlled component: it only reflects `currentDate`
  // (normalized to UTC midnight) and reports user clicks via onChange.
  const displayDate = useMemo(
    () => normalizeToUTCMidnight(currentDate || currentTime),
    [currentDate, currentTime],
  );

  // Where this session would land on its own — today while its cutoff is open,
  // otherwise the next date within the horizon that has a menu for it.
  const naturalSlot = useCallback(
    (session) =>
      resolveSessionSlot({
        session,
        availability,
        hubData,
        isEmployee,
        now: currentTime,
        windowItems,
      }),
    [availability, hubData, isEmployee, currentTime, windowItems],
  );

  // A session the user explicitly moved forward stays where they put it.
  const resolveSession = useCallback(
    (session) => {
      const natural = naturalSlot(session);
      if (
        currentSession === session &&
        natural.status !== "none" &&
        dateKey(displayDate) !== dateKey(natural.dateObj) &&
        hasMenu(availability, displayDate, session)
      ) {
        return { status: "open", dateObj: displayDate };
      }
      return natural;
    },
    [naturalSlot, currentSession, displayDate, availability],
  );

  // First date after `afterDate` that still has a menu for this session
  const nextSlotAfter = useCallback(
    (session, afterDate) => {
      if (!afterDate) return null;
      const offset = Math.round(
        (afterDate.getTime() - todayObj.getTime()) / (24 * 60 * 60 * 1000),
      );
      for (let i = offset + 1; i <= HORIZON_DAYS; i++) {
        const dateObj = addDays(todayObj, i);
        if (hasMenu(availability, dateObj, session)) return dateObj;
      }
      return null;
    },
    [availability, todayObj],
  );

  const getSessionIcon = (session) => sessionConfig[session]?.icon;

  // Nothing orderable in any session, anywhere in the horizon
  const allUnavailable = SESSION_ORDER.every(
    (s) => resolveSession(s).status === "none",
  );

  // Distinguishes "menus existed today but every window closed" from "nothing published at all"
  const anyMenuToday = SESSION_ORDER.some((s) =>
    hasMenu(availability, todayObj, s),
  );

  // Forward / back rows for the selected session
  const selectedState = currentSession ? resolveSession(currentSession) : null;
  const selectedNatural = currentSession ? naturalSlot(currentSession) : null;

  // Both rows apply to a live window slot too — a window being open is no reason
  // to hide the way into tomorrow's menu.
  const forwardSlot =
    selectedState && selectedState.status !== "none"
      ? nextSlotAfter(currentSession, selectedState.dateObj)
      : null;

  const backSlot =
    selectedState &&
    selectedState.status !== "none" &&
    selectedNatural?.status !== "none" &&
    dateKey(selectedState.dateObj) !== dateKey(selectedNatural.dateObj)
      ? selectedNatural.dateObj
      : null;

  useEffect(() => {
    onAvailabilityChange?.(loading || menuLoading ? false : allUnavailable);
  }, [allUnavailable, loading, menuLoading]);

  // Skeleton cards, not a "Loading availability..." line: the cards keep their place in
  // the layout, so nothing jumps when the real ones arrive.
  if (loading || menuLoading) {
    return (
      <div className="date-session-wrapper">
        <div className="session-container">
          {SESSION_ORDER.map((session) => (
            <div
              key={session}
              className="session-card session-card--skeleton"
              aria-hidden="true"
            >
              <div className="skeleton-icon" />
              <div className="skeleton-title" />
              <div className="skeleton-pill" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="date-session-wrapper">
      <div className="session-container">
        {SESSION_ORDER.map((session) => {
          const Icon = getSessionIcon(session);
          const state = resolveSession(session);

          // No orderable menu within the next 14 days → Serving Soon
          if (state.status === "none") {
            return (
              <div
                key={session}
                className="session-card session-card--unavailable"
                title="Menu not available yet for this session"
              >
                <div className="session-icon session-icon--muted">
                  <Icon size={24} />
                </div>
                <div className="session-info">
                  <div className="session-title session-title--muted">
                    {sessionConfig[session].displayName}
                  </div>
                  <div className="session-unavail-badge">Serving Soon</div>
                  <div className="session-unavail-text">Menu drops soon!</div>
                </div>
              </div>
            );
          }

          const isActive =
            currentSession === session &&
            dateKey(displayDate) === dateKey(state.dateObj);

          const label = dayLabel(state.dateObj, currentTime);
          const isToday = label === "Today";

          return (
            <div
              key={session}
              className={`session-card ${isActive ? "active" : ""}`}
              onClick={() => onChange(state.dateObj, session)}
              title={`Order ${label.toLowerCase()}'s ${session.toLowerCase()}`}
              style={{ cursor: "pointer" }}
            >
              <div className="session-icon">
                <Icon size={24} />
              </div>
              <div className="session-info">
                <div className="session-title">
                  {sessionConfig[session].displayName}
                </div>
                {/* Logged-out: plain session name only — no labels or pills.
                    Selected card (any status): plain session name only — no
                    "Order by" + cutoff pill.
                    Unselected: day pill; "Pre-order ·" text except for window. */}
                {!user || isActive ? null : (
                  <>
                    {state.status !== "window" && (
                      <div className="session-preorder-label">Pre-order ·</div>
                    )}
                    <div
                      className={`session-day-pill ${
                        isToday ? "today" : "later"
                      }`}
                    >
                      {label}
                    </div>
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {backSlot && (
        <button
          type="button"
          className="tomorrow-switch"
          onClick={() => onChange(backSlot, currentSession)}
        >
          <span className="tomorrow-switch-lead">
            Viewing {dayLabel(displayDate, currentTime).toLowerCase()}'s menu.
          </span>{" "}
          <span className="tomorrow-switch-cta">
            ← Back to {dayLabel(backSlot, currentTime).toLowerCase()}'s{" "}
            {currentSession.toLowerCase()}
          </span>
        </button>
      )}

      {forwardSlot && (
        <button
          type="button"
          className="tomorrow-switch"
          onClick={() => onChange(forwardSlot, currentSession)}
        >
          <span className="tomorrow-switch-lead">
            Not keen on{" "}
            {dayLabel(selectedState.dateObj, currentTime).toLowerCase()}'s
            choices?
          </span>{" "}
          <span className="tomorrow-switch-cta">
            See {dayLabel(forwardSlot, currentTime).toLowerCase()}'s{" "}
            {currentSession.toLowerCase()} instead →
          </span>
        </button>
      )}

      {allUnavailable &&
        (anyMenuToday ? (
          <div className="no-session-banner">
            <div className="no-session-icon-badge">
              <MdOutlineTimer className="no-session-icon" />
            </div>
            <div className="no-session-title">That's a wrap for today</div>
            <div className="no-session-sub">
              All ordering windows are closed for today. We'll be open bright
              and early tomorrow.
            </div>
          </div>
        ) : noAddress ? (
          /* User is logged in but has no delivery address — show address CTA */
          <div className="no-menu-hero">
            <img src={bowlImg} alt="" className="no-menu-hero-img" />
            <div className="no-menu-hero-title">Set your delivery address</div>
            <div className="no-menu-hero-sub">
              Add your address so we can show you fresh meals available near
              you.
            </div>
            <button
              onClick={() => navigate("/location")}
              style={{
                marginTop: "16px",
                backgroundColor: "#6B8E23",
                color: "white",
                padding: "12px 28px",
                borderRadius: "10px",
                border: "none",
                fontSize: "15px",
                fontWeight: "600",
                cursor: "pointer",
              }}
            >
              Add Address
            </button>
          </div>
        ) : (
          /* No menu published yet — kitchen is still preparing */
          <div className="no-menu-hero">
            <img src={bowlImg} alt="" className="no-menu-hero-img" />
            <div className="no-menu-hero-title">
              No menu available right now
            </div>
            <div className="no-menu-hero-sub">
              Our kitchen is working on something delicious!
              <br />
              We'll publish today's menu as soon as it's ready.
            </div>
            <div className="no-menu-hero-thanks">
              Thank you for your patience and understanding.
            </div>
            <FaHeart className="no-menu-hero-heart" />
          </div>
        ))}
    </div>
  );
};

export default DateSessionSelector;
